/**
 * Browser capability detection and helpers for speech-to-text functionality.
 */

// Web Speech API types (not in all TS libs)
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export type { SpeechRecognitionInstance, SpeechRecognitionEvent, SpeechRecognitionErrorEvent };

export type MicrophoneHelpKind = "permission" | "policy" | null;

export interface MicrophoneAccessIssue {
  kind:
    | "permission-denied"
    | "policy-blocked"
    | "insecure-context"
    | "device-not-found"
    | "device-busy"
    | "unsupported"
    | "unknown";
  message: string;
  helpKind: MicrophoneHelpKind;
}

let microphonePolicyBlockedCache: boolean | null = null;

function canUseGetUserMedia(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";
}

function createNamedError(name: string, message: string): Error {
  const error = new Error(message);
  error.name = name;
  return error;
}

function getErrorName(error: unknown): string | null {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "name" in error && typeof error.name === "string") {
    return error.name;
  }
  return null;
}

export function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionConstructor() !== null;
}

export function isMediaRecorderSupported(): boolean {
  return typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined" && canUseGetUserMedia();
}

export function isVoiceInputSupported(): boolean {
  return isSpeechRecognitionSupported() || isMediaRecorderSupported();
}

export function getMicrophoneSupportIssue(): MicrophoneAccessIssue | null {
  if (typeof window === "undefined") return null;

  if (!window.isSecureContext) {
    return {
      kind: "insecure-context",
      message: "Microphone access only works on HTTPS or localhost.",
      helpKind: null,
    };
  }

  if (!canUseGetUserMedia()) {
    return {
      kind: "unsupported",
      message: "This browser session cannot access microphone devices.",
      helpKind: null,
    };
  }

  return null;
}

export async function requestMicrophonePermission(): Promise<MediaStream> {
  const supportIssue = getMicrophoneSupportIssue();

  if (supportIssue?.kind === "insecure-context") {
    throw createNamedError("SecurityError", supportIssue.message);
  }

  if (supportIssue?.kind === "unsupported") {
    throw createNamedError("NotSupportedError", supportIssue.message);
  }

  return navigator.mediaDevices.getUserMedia({ audio: true });
}

export async function checkMicrophonePermission(): Promise<PermissionState> {
  try {
    const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
    return result.state;
  } catch {
    // Firefox does not support querying microphone permission.
    return "prompt";
  }
}

export async function isMicrophoneBlockedByPolicy(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (microphonePolicyBlockedCache !== null) return microphonePolicyBlockedCache;

  try {
    const response = await fetch(window.location.origin, {
      method: "HEAD",
      credentials: "same-origin",
      cache: "no-store",
    });

    const permissionsPolicy = response.headers.get("permissions-policy")?.toLowerCase() ?? "";
    const featurePolicy = response.headers.get("feature-policy")?.toLowerCase() ?? "";

    microphonePolicyBlockedCache =
      /\bmicrophone\s*=\s*\(\s*\)/.test(permissionsPolicy) ||
      /\bmicrophone\s*=\s*\(\s*none\s*\)/.test(permissionsPolicy) ||
      /\bmicrophone\s+'none'/.test(featurePolicy) ||
      /\bmicrophone\s+none/.test(featurePolicy);

    return microphonePolicyBlockedCache;
  } catch {
    microphonePolicyBlockedCache = false;
    return false;
  }
}

export async function describeMicrophoneAccessError(error?: unknown): Promise<MicrophoneAccessIssue> {
  const supportIssue = getMicrophoneSupportIssue();
  if (supportIssue) return supportIssue;

  const errorName = getErrorName(error);

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return {
      kind: "device-not-found",
      message: "No microphone was found on this device.",
      helpKind: null,
    };
  }

  if (
    errorName === "NotReadableError" ||
    errorName === "TrackStartError" ||
    errorName === "AbortError"
  ) {
    return {
      kind: "device-busy",
      message: "The microphone is unavailable. Another app or an OS privacy setting may be blocking it.",
      helpKind: null,
    };
  }

  if (
    errorName === "NotAllowedError" ||
    errorName === "PermissionDeniedError" ||
    errorName === "not-allowed" ||
    errorName === "service-not-allowed"
  ) {
    if (await isMicrophoneBlockedByPolicy()) {
      return {
        kind: "policy-blocked",
        message: "Microphone access is disabled by this site's server policy. Browser site permissions cannot override it.",
        helpKind: "policy",
      };
    }

    return {
      kind: "permission-denied",
      message: "Microphone access is blocked for this site. Click the lock icon in your address bar, allow microphone access, then refresh and try again.",
      helpKind: "permission",
    };
  }

  if (errorName === "SecurityError") {
    return {
      kind: "insecure-context",
      message: "Microphone access only works on HTTPS or localhost.",
      helpKind: null,
    };
  }

  if (errorName === "NotSupportedError" || errorName === "TypeError") {
    return {
      kind: "unsupported",
      message: "This browser session cannot access microphone devices.",
      helpKind: null,
    };
  }

  return {
    kind: "unknown",
    message: "Voice input could not start. Please check your browser, microphone device, and OS privacy settings.",
    helpKind: null,
  };
}
